import { api } from './client';
import type { Envelope } from './types';

export type DateVoteValue = 'YES' | 'NO' | 'MAYBE';

export interface DateCandidate {
  id: string;
  meetingId: string;
  startAt: string;
  endAt: string | null;
  proposedByUserId: string;
  createdAt: string;
  voteSummary?: Record<DateVoteValue, number>;
  myVote?: DateVoteValue | null;
}

export async function listDateCandidates(meetingId: string) {
  const { data } = await api.get<Envelope<DateCandidate[]>>(
    `/meetings/${meetingId}/date-candidates`,
  );
  return data.data;
}

export async function createDateCandidate(meetingId: string, startAt: string, endAt?: string) {
  const { data } = await api.post<Envelope<DateCandidate>>(
    `/meetings/${meetingId}/date-candidates`,
    { startAt, endAt },
  );
  return data.data;
}

export async function voteDate(candidateId: string, vote: DateVoteValue | null) {
  await api.put(`/date-candidates/${candidateId}/vote`, { vote });
}

export async function confirmDate(meetingId: string, dateCandidateId: string) {
  await api.post(`/meetings/${meetingId}/date-confirm`, { dateCandidateId });
}

export type PlaceCategory = 'MEAL' | 'CAFE' | 'ACTIVITY' | 'ETC';
export type PlaceTimeType = 'ANYTIME' | 'RESERVATION_REQUIRED';

export interface PlaceCandidate {
  id: string;
  meetingId: string;
  scheduleItemId: string | null;
  name: string;
  category: PlaceCategory;
  url: string | null;
  address: string | null;
  memo: string | null;
  timeType: PlaceTimeType;
  confirmed: boolean;
  proposedByUserId: string;
  createdAt: string;
  voteCount: number;
  myVote: boolean | null;
  /** Longitude — present when the backend geo-coded the place */
  x?: string;
  /** Latitude — present when the backend geo-coded the place */
  y?: string;
}

export async function listPlaceCandidates(meetingId: string, scheduleItemId?: string | null) {
  const params = scheduleItemId ? `?scheduleItemId=${encodeURIComponent(scheduleItemId)}` : '';
  const { data } = await api.get<Envelope<PlaceCandidate[]>>(
    `/meetings/${meetingId}/place-candidates${params}`,
  );
  return data.data;
}

export async function createPlaceCandidate(
  meetingId: string,
  input: {
    name: string;
    category?: PlaceCategory;
    url?: string;
    address?: string;
    memo?: string;
    timeType?: PlaceTimeType;
    /** 좌표(카카오 x=경도, y=위도) — 있으면 거리·소요시간 계산에 사용 */
    x?: string;
    y?: string;
    scheduleItemId?: string;
  },
) {
  const { data } = await api.post<Envelope<PlaceCandidate>>(
    `/meetings/${meetingId}/place-candidates`,
    input,
  );
  return data.data;
}

export async function votePlace(placeCandidateId: string, yes: boolean) {
  await api.put(`/place-candidates/${placeCandidateId}/vote`, { vote: yes ? 'YES' : null });
}

export async function confirmPlace(placeCandidateId: string) {
  await api.post(`/place-candidates/${placeCandidateId}/confirm`);
}

export async function unconfirmPlace(placeCandidateId: string) {
  await api.post(`/place-candidates/${placeCandidateId}/unconfirm`);
}

export interface VoteSettings {
  autoConfirmDate: boolean;
}

export async function getVoteSettings(meetingId: string): Promise<VoteSettings> {
  const { data } = await api.get<Envelope<VoteSettings>>(`/meetings/${meetingId}/vote-settings`);
  return data.data;
}

/** 호스트 전용. */
export async function updateVoteSettings(
  meetingId: string,
  settings: VoteSettings,
): Promise<VoteSettings> {
  const { data } = await api.put<Envelope<VoteSettings>>(
    `/meetings/${meetingId}/vote-settings`,
    settings,
  );
  return data.data;
}
